import {Checkbox, Input, Select} from 'antd'
import classNames from 'classnames'
import {
    game_createGameQuestionOption_Api,
    game_deleteGameQuestionOption_Api,
    game_editGameQuestionOption_Api,
    game_sortGameQuestionOptions_Api,
} from 'apis'
import useAsyncAction from 'modules/asyncCache/useAsyncAction'
import useTranslate from 'modules/local/useTranslate'
import React, {useState} from 'react'
import {notifyOnError} from '../../AppLocals/factory/createErrorEvent'
import {BiTrash} from 'react-icons/bi'
import {HiPlus} from 'react-icons/hi2'
import {TbChevronDown, TbChevronUp} from 'react-icons/tb'

// Panel quản lý đáp án của 1 câu hỏi - mỗi thay đổi gọi API ngay (add/edit/delete từng dòng),
// không dùng cơ chế diff-và-lưu-hàng-loạt để giữ đơn giản, khớp đúng API biên soạn theo dòng
// đã thiết kế ở backend (GameQuestionController).
export const GameQuestionOptionsPanel = ({question, options, onRefresh}) => {
    const t = useTranslate()
    const answerType = question?.answer_type

    const {handleAsyncAction: createOption, loading: creating} = useAsyncAction({
        apiInfo: game_createGameQuestionOption_Api,
        onSuccess: onRefresh,
        onError: notifyOnError(t),
    })
    const {handleAsyncAction: editOption} = useAsyncAction({
        apiInfo: game_editGameQuestionOption_Api,
        onSuccess: onRefresh,
        onError: notifyOnError(t),
    })
    const {handleAsyncAction: deleteOption} = useAsyncAction({
        apiInfo: game_deleteGameQuestionOption_Api,
        onSuccess: onRefresh,
        onError: notifyOnError(t),
    })
    const {handleAsyncAction: sortOptions} = useAsyncAction({
        apiInfo: game_sortGameQuestionOptions_Api,
        onSuccess: onRefresh,
        onError: notifyOnError(t),
    })

    const [newTitle, setNewTitle] = useState('')
    const [newRightTitle, setNewRightTitle] = useState('')
    const [newRightRefId, setNewRightRefId] = useState()

    const Wrapper = ({children, title}) => (
        <div className="p-3 flex flex-col gap-2 bg-slate-50 rounded-xl">
            <span className="font-semibold text-xs text-gray-600 uppercase tracking-wide">
                {t(title)}
            </span>
            {children}
        </div>
    )

    if (answerType === 'crossword') {
        const answer = options?.[0]
        return (
            <Wrapper title="correct answer">
                <Input
                    defaultValue={answer?.title}
                    placeholder={t('correct answer placeholder')}
                    className="rounded-lg"
                    onBlur={(e) => {
                        const value = e.target.value
                        if (!value) return
                        if (answer) editOption({title: value, is_correct: 1}, {id: answer.id})
                        else createOption({title: value, is_correct: 1, order_sort: 1}, {id: question.id})
                    }}
                />
            </Wrapper>
        )
    }

    if (answerType === 'single_choice') {
        return (
            <Wrapper title="answer options">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                    {(options ?? []).map((option) => (
                        <div
                            key={option.id}
                            className={classNames(
                                'flex items-center gap-2 px-2 py-1.5 rounded-lg border',
                                option.is_correct === 1 ? 'border-green-300 bg-green-50' : 'border-color-100'
                            )}>
                            <Checkbox
                                checked={option.is_correct === 1}
                                onChange={(e) => editOption({is_correct: e.target.checked ? 1 : 0}, {id: option.id})}
                            />
                            <Input
                                defaultValue={option.title}
                                bordered={false}
                                className="flex-1 px-1"
                                onBlur={(e) => e.target.value !== option.title && editOption({title: e.target.value}, {id: option.id})}
                            />
                            <button type="button" onClick={() => deleteOption({}, {id: option.id})} className="flex-shrink-0 text-red-400 hover:text-red-500 cursor-pointer">
                                <BiTrash size={14}/>
                            </button>
                        </div>
                    ))}
                </div>
                <div className="flex items-center gap-2 pt-2 border-t border-color-100">
                    <Input
                        value={newTitle}
                        placeholder={t('new option placeholder')}
                        className="rounded-lg"
                        onChange={(e) => setNewTitle(e.target.value)}
                        onPressEnter={() => {
                            if (!newTitle.trim()) return
                            createOption({title: newTitle, order_sort: (options?.length ?? 0) + 1}, {id: question.id})
                            setNewTitle('')
                        }}
                    />
                    <button
                        type="button"
                        disabled={creating || !newTitle.trim()}
                        onClick={() => {
                            createOption({title: newTitle, order_sort: (options?.length ?? 0) + 1}, {id: question.id})
                            setNewTitle('')
                        }}
                        className="flex-shrink-0 flex items-center gap-1 text-purple-600 font-semibold text-xs cursor-pointer disabled:opacity-40">
                        <HiPlus size={14}/>{t('add option')}
                    </button>
                </div>
            </Wrapper>
        )
    }

    if (answerType === 'ordering') {
        const sorted = [...(options ?? [])].sort((a, b) => a.order_sort - b.order_sort)
        const move = (index, dir) => {
            const newIndex = index + dir
            if (newIndex < 0 || newIndex >= sorted.length) return
            const next = [...sorted]
            const tmp = next[index]
            next[index] = next[newIndex]
            next[newIndex] = tmp
            sortOptions({game_question_option_ids: next.map((o) => o.id).join(',')}, {id: question.id})
        }
        return (
            <Wrapper title="items in correct order">
                {sorted.map((option, index) => (
                    <div key={option.id} className="flex items-center gap-2 px-2 py-1.5 rounded-lg border border-color-100">
                        <span className="flex-shrink-0 w-5 h-5 flex items-center justify-center rounded-full bg-purple-50 text-2xs font-bold text-purple-700">{index + 1}</span>
                        <Input
                            defaultValue={option.title}
                            bordered={false}
                            className="flex-1 px-1"
                            onBlur={(e) => e.target.value !== option.title && editOption({title: e.target.value}, {id: option.id})}
                        />
                        <button type="button" onClick={() => move(index, -1)} disabled={index === 0} className="text-color-400 disabled:opacity-30 cursor-pointer">
                            <TbChevronUp size={16}/>
                        </button>
                        <button type="button" onClick={() => move(index, 1)} disabled={index === sorted.length - 1} className="text-color-400 disabled:opacity-30 cursor-pointer">
                            <TbChevronDown size={16}/>
                        </button>
                        <button type="button" onClick={() => deleteOption({}, {id: option.id})} className="text-red-400 hover:text-red-500 cursor-pointer">
                            <BiTrash size={14}/>
                        </button>
                    </div>
                ))}
                <div className="flex items-center gap-2 pt-2 border-t border-color-100">
                    <Input
                        value={newTitle}
                        placeholder={t('new item placeholder')}
                        className="rounded-lg"
                        onChange={(e) => setNewTitle(e.target.value)}
                    />
                    <button
                        type="button"
                        disabled={creating || !newTitle.trim()}
                        onClick={() => {
                            createOption({title: newTitle, order_sort: sorted.length + 1}, {id: question.id})
                            setNewTitle('')
                        }}
                        className="flex-shrink-0 flex items-center gap-1 text-purple-600 font-semibold text-xs cursor-pointer disabled:opacity-40">
                        <HiPlus size={14}/>{t('add item')}
                    </button>
                </div>
            </Wrapper>
        )
    }

    if (answerType === 'matching') {
        const leftItems = (options ?? []).filter((o) => !o.ref_id)
        const rightItems = (options ?? []).filter((o) => !!o.ref_id)
        return (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 p-4 background border border-color-100 rounded-xl">
                <Wrapper title="left side prompts">
                    {leftItems.map((option) => (
                        <div key={option.id} className="flex items-center gap-2 px-2 py-1.5 rounded-lg border border-color-100">
                            <Input
                                defaultValue={option.title}
                                bordered={false}
                                className="flex-1 px-1"
                                onBlur={(e) => e.target.value !== option.title && editOption({title: e.target.value}, {id: option.id})}
                            />
                            <button type="button" onClick={() => deleteOption({}, {id: option.id})} className="flex-shrink-0 text-red-400 hover:text-red-500 cursor-pointer">
                                <BiTrash size={14}/>
                            </button>
                        </div>
                    ))}
                    <div className="flex items-center gap-2 pt-2 border-t border-color-100">
                        <Input
                            value={newTitle}
                            placeholder={t('new prompt placeholder')}
                            className="rounded-lg"
                            onChange={(e) => setNewTitle(e.target.value)}
                        />
                        <button
                            type="button"
                            onClick={() => {
                                if (!newTitle.trim()) return
                                createOption({title: newTitle, order_sort: leftItems.length + 1}, {id: question.id})
                                setNewTitle('')
                            }}
                            className="flex-shrink-0 text-purple-600 cursor-pointer">
                            <HiPlus size={16}/>
                        </button>
                    </div>
                </Wrapper>
                <Wrapper title="right side answers">
                    {rightItems.map((option) => (
                        <div key={option.id} className="flex items-center gap-2 px-2 py-1.5 rounded-lg border border-color-100">
                            <Input
                                defaultValue={option.title}
                                bordered={false}
                                className="flex-1 px-1"
                                onBlur={(e) => e.target.value !== option.title && editOption({title: e.target.value}, {id: option.id})}
                            />
                            <Select
                                value={option.ref_id}
                                className="w-24 flex-shrink-0"
                                onChange={(v) => editOption({ref_id: v}, {id: option.id})}
                                options={leftItems.map((l) => ({label: l.title, value: l.id}))}
                            />
                            <button type="button" onClick={() => deleteOption({}, {id: option.id})} className="flex-shrink-0 text-red-400 hover:text-red-500 cursor-pointer">
                                <BiTrash size={14}/>
                            </button>
                        </div>
                    ))}
                    <div className="flex items-center gap-2 pt-2 border-t border-color-100">
                        <Input
                            value={newRightTitle}
                            placeholder={t('new answer placeholder')}
                            className="rounded-lg"
                            onChange={(e) => setNewRightTitle(e.target.value)}
                        />
                        <Select
                            value={newRightRefId}
                            placeholder={t('pairs with')}
                            className="w-24 flex-shrink-0"
                            onChange={setNewRightRefId}
                            options={leftItems.map((l) => ({label: l.title, value: l.id}))}
                        />
                        <button
                            type="button"
                            onClick={() => {
                                if (!newRightTitle.trim() || !newRightRefId) return
                                createOption({title: newRightTitle, ref_id: newRightRefId, order_sort: rightItems.length + 1}, {id: question.id})
                                setNewRightTitle('')
                                setNewRightRefId(undefined)
                            }}
                            className="flex-shrink-0 text-purple-600 cursor-pointer">
                            <HiPlus size={16}/>
                        </button>
                    </div>
                </Wrapper>
            </div>
        )
    }

    return null
}

export default GameQuestionOptionsPanel
